import React, { useRef, useEffect, useCallback, useState} from 'react';
import {MODULATOR_CONTROL} from 'constants/contextual-help.js';
import Documented from 'components/help/Documented.js';
import {connect} from 'react-redux';
import {useStyleAndClass} from './useStyleAndClass.js';

const DOUBLE_CLICK = 300;

const ModulatorControl = ({envelope, lfo, parameter, sequencerEngine, keyScaling}) => { 
    let ref = useRef(undefined);
    let lastClick = useRef(0);
    let [draggingRight, setDraggingRight] = useState(false);
    let [draggingLeft, setDraggingLeft] = useState(false);
    let [zoomMin, setZoomMin] = useState(parameter.min);
    let [zoomMax, setZoomMax] = useState(parameter.max);
    let styleAndClass = useStyleAndClass({parameter, envelope, lfo, sequencerEngine, keyScaling, zoomMin, zoomMax});
    let [min, setMin] = useState(styleAndClass && styleAndClass.mod.min);
    let [max, setMax] = useState(styleAndClass && styleAndClass.mod.max);

    let mod = styleAndClass && styleAndClass.mod;

    const clearDragging = useCallback(() => {
        if (draggingLeft || draggingRight) {
            setDraggingLeft(false);
            setDraggingRight(false);
        }
    }, [ draggingLeft, draggingRight, setDraggingLeft, setDraggingRight]);

    const onMouseMove = useCallback((e) => {
        if (draggingLeft || draggingRight) {
            let rect = ref.current.getBoundingClientRect();
            let x = e.clientX - rect.left;
            let width = rect.width;
            let widthRatio = x / width;
            let range = zoomMax  - zoomMin;
            let newPt = zoomMin + range * widthRatio
            if (draggingLeft) {
                mod.min = newPt;
                setMin(newPt);
            } else {
                mod.max = newPt;
                setMax(newPt);
            }
        }
    }, [
        onMouseMove,
        parameter,
        mod,
        draggingLeft,
        draggingRight,
        setMin,
        setMax,
        zoomMin,
        zoomMax]);

    const round = (x) => {
        if (mod.max - mod.min >= 1000) {
            return Math.round(x);
        } else if (mod.max - mod.min >= 100) {
            return Math.round(x*10) / 10;
        } else {
            return Math.round(x*10000) / 10000;
        }
        return x;
    };

    const preventDragging = (dragging) => {
        let node = ref.current
            .parentNode
            .parentNode
            .parentNode
            .parentNode
            .parentNode;
        let found = 0;
        for (let i=0; i < 12; i++) {
            if (node.className.includes("effect") && !node.className.includes("inner-effect") &&
                !node.className.includes("effects-view") &&
                !node.className.includes("effect-rack")) {
                if (found === 0 && (parameter.context.effect && parameter.context.effect.name === 'Effect Rack')) {
                    found++;
                    node.setAttribute("draggable", dragging);
                    node = node.parentNode;
                    continue;
                }
                node.setAttribute("draggable", dragging);
                return;
            }
            node = node.parentNode;
        }
    };

    const zoom = useCallback(() => {
        let midMin = (min + zoomMin) / 2;
        let midMax = (max + zoomMax) / 2;
        setZoomMin(midMin);
        setZoomMax(midMax);
    }, [setZoomMin, setZoomMax, min, max, zoomMin, zoomMax]);

    const onMouseDown = useCallback((e) => {
        let now = new Date().getTime();

        if (now - lastClick.current <= DOUBLE_CLICK) {
            zoom();
            return;
        }
        lastClick.current = now;

        let rect = ref.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let width = rect.width;
        let widthRatio = x / width;
        let range = zoomMax - zoomMin;
        let newPt = zoomMin + range * widthRatio
        if (newPt > (mod.max + mod.min) / 2) {
            setDraggingRight(true);
        } else {
            setDraggingLeft(true);
        }
    }, [mod, setDraggingLeft, setDraggingRight, zoomMin, zoomMax, setZoomMin, setZoomMax, max, min]);

    const onMouseUp = useCallback((e) => {
        if (sequencerEngine.state.keyboard.isCommandDown) {
            let _min = mod.min;
            let _max = mod.max;
            mod.min = _max;
            mod.max = _min;
            setMin(_max);
            setMax(_min);
        }
        clearDragging(e);
    }, [setMin, setMax, draggingLeft, draggingRight, setDraggingLeft, setDraggingRight]);

    if (!styleAndClass) {
        return (<div></div>);
    }

    let {style, className, num, thumbClass} = styleAndClass;
    
    return (
        <Documented className="modulator-control-container" element={MODULATOR_CONTROL}>
          <div
            draggable={true}
            onDragStart={(e) => e.preventDefault()}
            ref={ref}
            onMouseUp={onMouseUp} 
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseLeave={clearDragging}
            className="modulator-control">
            <div className={className} style={style}>
            <div
              onMouseDown={(e) => {
                  e.stopPropagation();
                  if (mod.min < mod.max) {
                      setDraggingLeft(true);
                  } else {
                      setDraggingRight(true);
                  }
              }}
              className={mod.min < mod.max ? "small left-edge" : "big left-edge"}></div>
              <div
                onMouseDown={(e) => {
                    e.stopPropagation();
                    if (mod.min < mod.max) {
                        setDraggingRight(true);
                    } else {
                        setDraggingLeft(true);
                    }
                }}
                className={mod.min < mod.max ? "big right-edge" : "small right-edge"}></div>
            </div>
            <div className="mod-number">
              {draggingLeft ? "Min: " + round(mod.min) : draggingRight ? "Max: " + round(mod.max) : ''}
            </div>
          </div>
        </Documented>
    );
}

const mapStateToProps = (state, props) => {
  return {
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ModulatorControl);
